import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
const PromoSessionContext = createContext(null);
export const usePromoSession = () => {
    const context = useContext(PromoSessionContext);
    if (!context) {
        throw new Error('usePromoSession must be used within a PromoSessionProvider');
    }
    return context;
};
export const PromoSessionProvider = ({ children }) => {
    // Initialize state with data from sessionStorage
    const getInitialData = useCallback(() => {
        if (globalThis.window === undefined) {
            return {
                onLoadPromoCheck: null,
                promoErrorType: null,
                newCustomerPromoCode: null,
                appliedPromoCount: null,
            };
        }
        return {
            onLoadPromoCheck: sessionStorage.getItem('onLoadPromoCheck'),
            promoErrorType: sessionStorage.getItem('promoErrorType'),
            newCustomerPromoCode: sessionStorage.getItem('newCustomerPromoCode'),
            appliedPromoCount: sessionStorage.getItem('appliedPromoCount'),
        };
    }, []);
    const [data, setData] = useState(getInitialData);
    // Helper function to get data from sessionStorage
    const getSessionData = useCallback(() => {
        if (globalThis.window === undefined) {
            return {
                onLoadPromoCheck: null,
                promoErrorType: null,
                newCustomerPromoCode: null,
                appliedPromoCount: null,
            };
        }
        return {
            onLoadPromoCheck: sessionStorage.getItem('onLoadPromoCheck'),
            promoErrorType: sessionStorage.getItem('promoErrorType'),
            newCustomerPromoCode: sessionStorage.getItem('newCustomerPromoCode'),
            appliedPromoCount: sessionStorage.getItem('appliedPromoCount'),
        };
    }, []);
    // Update state when sessionStorage changes
    const updateDataFromSession = useCallback(() => {
        setData(getSessionData());
    }, [getSessionData]);
    const setOnLoadPromoCheck = useCallback((value) => {
        if (globalThis.window === undefined)
            return;
        if (value === null) {
            sessionStorage.removeItem('onLoadPromoCheck');
        }
        else {
            sessionStorage.setItem('onLoadPromoCheck', value);
        }
        updateDataFromSession();
    }, [updateDataFromSession]);
    const setPromoErrorType = useCallback((value) => {
        if (globalThis.window === undefined)
            return;
        if (value === null) {
            sessionStorage.removeItem('promoErrorType');
        }
        else {
            sessionStorage.setItem('promoErrorType', value);
        }
        updateDataFromSession();
    }, [updateDataFromSession]);
    const setNewCustomerPromoCode = useCallback((value) => {
        if (globalThis.window === undefined)
            return;
        if (value === null) {
            sessionStorage.removeItem('newCustomerPromoCode');
        }
        else {
            sessionStorage.setItem('newCustomerPromoCode', value);
        }
        updateDataFromSession();
    }, [updateDataFromSession]);
    const setAppliedPromoCount = useCallback((value) => {
        if (globalThis.window === undefined)
            return;
        if (value === null) {
            sessionStorage.removeItem('appliedPromoCount');
        }
        else {
            sessionStorage.setItem('appliedPromoCount', value);
        }
        updateDataFromSession();
    }, [updateDataFromSession]);
    const getAppliedPromoCount = useCallback(() => {
        if (globalThis.window === undefined)
            return 0;
        const count = sessionStorage.getItem('appliedPromoCount');
        return count ? Number.parseInt(count, 10) : 0;
    }, []);
    const incrementAppliedPromoCount = useCallback(() => {
        const currentCount = getAppliedPromoCount();
        const newCount = currentCount + 1;
        setAppliedPromoCount(newCount.toString());
        return newCount;
    }, [getAppliedPromoCount, setAppliedPromoCount]);
    const clearPromoSession = useCallback(() => {
        if (globalThis.window === undefined)
            return;
        sessionStorage.removeItem('onLoadPromoCheck');
        sessionStorage.removeItem('promoErrorType');
        sessionStorage.removeItem('newCustomerPromoCode');
        // Note: We typically don't clear appliedPromoCount as it's used for analytics
        updateDataFromSession();
    }, [updateDataFromSession]);
    // Listen for storage events from other tabs/windows
    useEffect(() => {
        if (globalThis.window === undefined)
            return;
        const handleStorageChange = (e) => {
            if (e.storageArea === sessionStorage &&
                (e.key === 'onLoadPromoCheck' ||
                    e.key === 'promoErrorType' ||
                    e.key === 'newCustomerPromoCode' ||
                    e.key === 'appliedPromoCount')) {
                updateDataFromSession();
            }
        };
        globalThis.window.addEventListener('storage', handleStorageChange);
        return () => globalThis.window.removeEventListener('storage', handleStorageChange);
    }, [updateDataFromSession]);
    const contextValue = useMemo(() => ({
        data,
        setOnLoadPromoCheck,
        setPromoErrorType,
        setNewCustomerPromoCode,
        setAppliedPromoCount,
        getAppliedPromoCount,
        incrementAppliedPromoCount,
        clearPromoSession,
    }), [
        data,
        setOnLoadPromoCheck,
        setPromoErrorType,
        setNewCustomerPromoCode,
        setAppliedPromoCount,
        getAppliedPromoCount,
        incrementAppliedPromoCount,
        clearPromoSession,
    ]);
    return _jsx(PromoSessionContext.Provider, { value: contextValue, children: children });
};
